import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { LogOut, Settings, LayoutGrid, ExternalLink, Handshake, Trophy, ShoppingBag, Link2, Sparkles, KeyRound, Inbox, Bell, ClipboardList, Users, Swords, CircleDot, Radio, Youtube } from "lucide-react";
import api, { LOGO_URL } from "@/lib/api";
import { Seo } from "@/components/Seo";
import { useAuth } from "@/context/AuthContext";
import { SiteSettings } from "@/components/admin/SiteSettings";
import { DivisionsManager } from "@/components/admin/DivisionsManager";
import { SponsorsManager } from "@/components/admin/SponsorsManager";
import { TournamentsManager } from "@/components/admin/TournamentsManager";
import { MerchandiseManager } from "@/components/admin/MerchandiseManager";
import { LinksManager } from "@/components/admin/LinksManager";
import { PromosManager } from "@/components/admin/PromosManager";
import { AccountManager } from "@/components/admin/AccountManager";
import { MessagesManager } from "@/components/admin/MessagesManager";
import { NotificationsManager } from "@/components/admin/NotificationsManager";
import { RegistrationsManager } from "@/components/admin/RegistrationsManager";
import { SpareListManager } from "@/components/admin/SpareListManager";
import { BraggersManager } from "@/components/admin/BraggersManager";
import { OrbitManager } from "@/components/admin/OrbitManager";
import { ChallongeManager } from "@/components/admin/ChallongeManager";
import { VideosManager } from "@/components/admin/VideosManager";

export default function Dashboard() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("divisions");
  const [unread, setUnread] = useState(0);
  const [pendingRegs, setPendingRegs] = useState(0);
  const [pendingSpares, setPendingSpares] = useState(0);

  useEffect(() => {
    api.get("/admin/messages").then((r) => setUnread(r.data.unread || 0)).catch(() => {});
    api.get("/admin/registrations").then((r) => setPendingRegs(r.data.pending || 0)).catch(() => {});
    api.get("/admin/spares").then((r) => setPendingSpares(r.data.pending || 0)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-[#F9FAF8]">
      <Seo title="Admin Dashboard | Quinte BCA Pool League" path="/admin" noindex />
      <header className="bg-[#0B0B0C] text-white sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <img src={LOGO_URL} alt="Quinte BCA Pool League" className="h-10 w-10 object-contain" />
            <span className="font-heading font-extrabold">Admin Dashboard</span>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/" target="_blank" className="hidden sm:inline-flex items-center gap-1 text-sm text-gray-300 hover:text-[#E23744]" data-testid="view-site-link">
              View site <ExternalLink className="h-4 w-4" />
            </Link>
            <span className="hidden sm:inline text-sm text-gray-300">{user?.email}</span>
            <button onClick={logout} data-testid="logout-btn" className="inline-flex items-center gap-1 bg-white/10 hover:bg-white/20 rounded-md px-3 py-1.5 text-sm">
              <LogOut className="h-4 w-4" /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-wrap gap-2 mb-8 border-b border-[#E5E7EB]">
          {[
            { id: "divisions", label: "Divisions & Stats", icon: LayoutGrid },
            { id: "messages", label: "Messages", icon: Inbox, badge: unread },
            { id: "notifications", label: "Notifications", icon: Bell },
            { id: "registrations", label: "Registrations", icon: ClipboardList, badge: pendingRegs },
            { id: "spares", label: "Spare List", icon: Users, badge: pendingSpares },
            { id: "site", label: "Site Content", icon: Settings },
            { id: "sponsors", label: "Partners", icon: Handshake },
            { id: "tournaments", label: "Tournaments", icon: Trophy },
            { id: "braggers", label: "Bragger's Cup", icon: Swords },
            { id: "promos", label: "News/Promos", icon: Sparkles },
            { id: "merchandise", label: "Merchandise", icon: ShoppingBag },
            { id: "orbit", label: "ORBIT Chalk", icon: CircleDot },
            { id: "challonge", label: "Live Tournaments", icon: Radio },
            { id: "videos", label: "Videos", icon: Youtube },
            { id: "links", label: "Links", icon: Link2 },
            { id: "account", label: "Account", icon: KeyRound },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`tab-${t.id}`}
              className={`inline-flex items-center gap-2 px-4 py-3 font-medium border-b-2 -mb-px transition-colors ${
                tab === t.id ? "border-[#1E63C4] text-[#1E63C4]" : "border-transparent text-[#555555] hover:text-[#111111]"
              }`}
            >
              <t.icon className="h-4 w-4" /> {t.label}
              {t.badge ? (
                <span className="inline-flex items-center justify-center rounded-full bg-[#E11D2A] text-white text-[10px] font-bold h-5 min-w-[20px] px-1" data-testid="messages-tab-badge">
                  {t.badge}
                </span>
              ) : null}
            </button>
          ))}
        </div>

        {tab === "divisions" && <DivisionsManager />}
        {tab === "messages" && <MessagesManager onUnreadChange={setUnread} />}
        {tab === "notifications" && <NotificationsManager />}
        {tab === "registrations" && <RegistrationsManager onPendingChange={setPendingRegs} />}
        {tab === "spares" && <SpareListManager onPendingChange={setPendingSpares} />}
        {tab === "site" && <SiteSettings />}
        {tab === "sponsors" && <SponsorsManager />}
        {tab === "tournaments" && <TournamentsManager />}
        {tab === "braggers" && <BraggersManager />}
        {tab === "promos" && <PromosManager />}
        {tab === "merchandise" && <MerchandiseManager />}
        {tab === "orbit" && <OrbitManager />}
        {tab === "challonge" && <ChallongeManager />}
        {tab === "videos" && <VideosManager />}
        {tab === "links" && <LinksManager />}
        {tab === "account" && <AccountManager />}
      </div>
    </div>
  );
}
